import { UserSummary } from './users.types'

export enum MarketBasketStatus {
	PENDING = 'PENDING',
	PROCESSING = 'PROCESSING',
	RECALCULATING = 'RECALCULATING',
	ANALYZED = 'ANALYZED',
	COMPLETED = 'COMPLETED',
	FAILED = 'FAILED',
}

export type MarketBasketSummary = {
	id: Id
	name: string
	displayName: string
	salesforceCaseId: string
	salesforceCaseNumber: string
	bepBrand: string
	status: MarketBasketStatus
	processingStatus: string
	progress: number | null
	analysisError: string | null
	analysisWarning: string | null
	createdAt: string
	completedAt: string | null
	deleted: boolean
	dateRangeStart: string
	dateRangeEnd: string
	effectiveDate: string
	analyst: string
	assignedAnalyst: UserSummary
	createdByDisplayName: string
	tier: string
	caseIdLink: string | null
	basketType: string
	sourceType: string
}

type UploadedFilesFields = {
	files?: string[]
	layout: string
	fileCustomer?: CategorySearchResult
	bepBrand: string
	fileDistributors?: SearchResultNode[]
}

type InternalCustomersFields = {
	internalCustomers: SearchResultNode[]
	activeCustomersOnly: boolean
	distributorsFilter?: SearchResultNode[]
	manufacturersFilter?: SearchResultNode[]
	categoriesFilter?: SearchResultNode[]
}

export type MarketBasket = MarketBasketSummary & {
	activeCustomersOnly: boolean
	analystId?: Id
	basketType: string
	blanketMarkupType?: string
	blanketMarkupAmount?: number
	categoriesFilter: SearchResultNode[]
	currency: string
	distributorsFilter: SearchResultNode[]
	manufacturersFilter: SearchResultNode[]
	marketSegment: string
	markupAmount: number
	markupType: string
	markupsByCategory?: { markupType: string; markup: string; category: CategorySearchResult }[]
	matchType: string
	displayName: string
	purchaseDateRangeEnd: string
	purchaseDateRangeStart: string
	reviewSavingsTolerance?: number
	sourceType: string
	allowNonContractMatches: boolean
	targetDistributor: SearchResultNode | null
	targetManufacturers: SearchResultNode[]
	ignoreDistributorRestrictions: boolean
	skippedTasks: boolean
} & (UploadedFilesFields | InternalCustomersFields)

export type MarketBasketWithSettings = MarketBasket & {
	settings: MarketBasketSettings
}

export type MarketBasketKpis = {
	matchedSpend: number | null
	matchedSpendPct: number | null
	similarMatchPriceSavings: number | null
	similarMatchPriceSavingsPct: number | null
	similarMatchRebates: number | null
	similarMatchRebatesPct: number | null
	similarMatchSavings: number | null
	similarMatchSavingsPct: number | null
	exactMatchPriceSavings: number | null
	exactMatchPriceSavingsPct: number | null
	exactMatchRebates: number | null
	exactMatchRebatesPct: number | null
	exactMatchSavings: number | null
	exactMatchSavingsPct: number | null
	savings: number | null
	savingsPct: number | null
	totalSpend: number | null
}

export type MarketBasketSettings = {
	packSize: {
		low: number
		high: number
	}
	switchDirectDeals: boolean
	highVolumeMfr: {
		enabled: boolean
		cases: number
		spend: number
	}
	sameManufacturer: boolean
	restrictedManufacturer: boolean
	preferExactMatch: boolean
	highestRebate: {
		enabled: boolean
		value: number
	}
	programs: {
		direct: boolean
		beBrandExtendable: boolean
		bePlatform: boolean
		entegra: boolean
	}
	tier: string
	caseQty: {
		enabled: boolean
		value: number
	}
	purchaseQuantity: {
		enabled: boolean
		value: number
	}
	savingsMoreThan: {
		type: string
		value: number
	}
	groupBy: string
	annualizationMonths: number
}

export type MarketBasketExclusion = {
	id: Id
	customer: {
		name: string
		plid: string
	}
	manufacturer: {
		name: string
		plid: string
	}
	programType: string
	canDelete: boolean
	canToggle: boolean
	enabled: boolean
}
